import type { WeatherSnapshot } from '../types';

function isRainCode(weatherCode: number) {
  return (
    (weatherCode >= 51 && weatherCode <= 67) ||
    (weatherCode >= 80 && weatherCode <= 82) ||
    (weatherCode >= 95 && weatherCode <= 99)
  );
}

function describeWeatherCode(weatherCode: number) {
  if (weatherCode === 0) return 'Clear';
  if ([1, 2, 3].includes(weatherCode)) return 'Cloudy';
  if ([45, 48].includes(weatherCode)) return 'Fog';
  if (weatherCode >= 51 && weatherCode <= 67) return 'Rain';
  if (weatherCode >= 71 && weatherCode <= 77) return 'Snow';
  if (weatherCode >= 80 && weatherCode <= 82) return 'Showers';
  if (weatherCode >= 95) return 'Storms';
  return 'Mixed conditions';
}

function getWeatherMultiplier(temperature: number, weatherCode: number) {
  if (isRainCode(weatherCode) || temperature < 15) {
    return {
      multiplier: 0.8,
      reason: isRainCode(weatherCode) ? 'rain slowing moisture loss' : 'cool air slowing moisture loss',
    };
  }
  if (temperature > 25) return { multiplier: 1.25, reason: 'heat increasing moisture loss' };
  return { multiplier: 1, reason: 'standard outdoor conditions' };
}

export async function fetchOpenMeteoWeather(latitude: number, longitude: number): Promise<WeatherSnapshot> {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
    current: 'temperature_2m,weather_code,rain,showers',
    timezone: 'auto',
  });
  const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`);
  if (!response.ok) throw new Error('Weather service unavailable.');
  const payload = await response.json();
  const temperature = Number(payload.current?.temperature_2m);
  const weatherCode = Number(payload.current?.weather_code);
  if (!Number.isFinite(temperature) || !Number.isFinite(weatherCode)) {
    throw new Error('Weather service returned an unexpected response.');
  }
  const modifier = getWeatherMultiplier(temperature, weatherCode);
  return {
    temperature,
    weatherCode,
    condition: describeWeatherCode(weatherCode),
    multiplier: modifier.multiplier,
    reason: modifier.reason,
    updatedAt: Date.now(),
    latitude,
    longitude,
  };
}
